import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import ResumeBanner from "@/components/ResumeBanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  { q: "How does it work?", a: "No. Nobody here promises you'll get rich quick. You can take the quiz, see your top 3 side hustles, and do the 7-day sprint for free, with no card needed. Pro is $19 a month and you can cancel anytime. If Pro isn't a fit, you can request a refund within 7 days of your first payment. Nothing here guarantees income. What you earn depends on your effort and your time." },
  { q: "I'm not good with computers. Can I still do it?", a: "Yes. If you can use a phone and send a text, you can use this app. Your coach explains every step in plain English, one step at a time, and you can ask it anything. Some paths, like a local service business, need very little computer work at all." },
  { q: "How much time does it take?", a: "You choose. Most people start with a few hours a week around their job. Your plan is built around the time you actually have, not the time you wish you had." },
  { q: "What kind of side hustles are there?", a: "There are 28 paths. Some are hands-on and local, like pressure washing or lawn care. Others are online, like digital products, tutoring, bookkeeping, video editing, making videos for brands, or running an AI influencer account. The quiz picks your top 3 based on your skills, your budget and your time. The free plan shows your top 3, and Pro opens all 28." },
  { q: "What happens to my data?", a: "We store your quiz answers and your coach chats so we can match your paths and personalize your coach. We do not sell your data. You can ask us to delete your account and data at any time by emailing info@ihatemyjob.app, and we aim to respond within seven days." },
  { q: "Is it free?", a: "Yes, you can start free. The quiz, your top 3 side hustles, and the 7-Day Launch Sprint with the text coach are all free. If you upgrade to Pro and it isn't a fit, request a refund within 7 days of your first payment. We review it and refund you in full." },
  { q: "Do I need experience?", a: "No. The quiz matches you to a path that fits the skills you already have, and your coach walks you through every step from day one. If you can follow a plan, you can do this." },
];

const SHOTS = [
  { id: "local-service", name: "Local Service Business" },
  { id: "ai-digital-products", name: "AI-Assisted Digital Products" },
  { id: "ugc-creator", name: "UGC Creator" },
  { id: "ai-influencer", name: "AI Influencer" },
];

const PathShots = () => (
  <section className="max-w-5xl mx-auto px-5 pb-8" data-testid="path-shots">
    <div className="eyebrow normal-case text-[#FD0F10]">Real screens from the app</div>
    <p className="mt-1 text-sm text-gray-300">Four of the 28 paths, exactly as they look inside the app. Your match scores and numbers depend on your own answers.</p>
    <div className="mt-4 -mx-5 px-5 flex sm:grid sm:grid-cols-4 gap-3 overflow-x-auto snap-x snap-mandatory scroll-px-5 pb-2">
      {SHOTS.map((s) => (
        <figure key={s.id} className="shrink-0 w-[62%] sm:w-auto snap-start" data-testid={`path-shot-${s.id}`}>
          <img src={`/screenshots/thumb-path-${s.id}.webp`} alt={`${s.name} path screen in the I Hate My Job app`} loading="lazy" width="390" height="844"
            className="w-full h-auto rounded-xl border border-[#2A2E39]" />
          <figcaption className="mt-2 text-sm text-white font-bold">{s.name}</figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const OUTCOMES = [
  { h: "You'll know exactly what to do next.", t: "You can stop guessing and stop scrolling through a hundred 'make money online' videos. You get your top 3 ways to make money, matched to you." },
  { h: "You can talk to your coach anytime, anywhere.", t: "On your way to work, talk to your coach hands-free about your plan and your next step. On your lunch break, pick up right where you left off. You don't need a desk or a free weekend, because you can build your way out in the time you already have.", small: "Voice coach is included with Pro." },
  { h: "Your coach works with you until you earn your first dollar.", t: "That's the whole point. It's not a dream of getting rich overnight. It's one real dollar you earned on your own, and then the next one. That first dollar changes how you see everything." },
];

const PAIN_POINTS = [
  { h: "You can't just quit.", t: "Bills don't stop because you hate your job. You have rent or a mortgage, kids, and other responsibilities. You need another option before you can walk away." },
  { h: "You're working for your family, but you're missing them.", t: "You're working hard to give them a better life, but you're missing the life that's happening right now. You want more time with your kids, more ordinary days together, and more of the moments you don't get back." },
  { h: "What if nothing changes?", t: "Picture the same alarm, the same job, and the same paycheck five, ten, or twenty years from now. The scary part isn't trying something new. It's never trying at all." },
];

const PLAN_BLOCKS = [
  { h: "Start free. Upgrade only if you want more.", t: "On the free plan, you take the quiz, get your top 3 paths, pick one, and do your 7-day sprint with the text coach. On Pro, all 28 paths open up, and you get the 24/7 voice coach." },
  { h: "Why not just buy an ebook?", t: "People already sell ebooks and courses on these same ways to make money. But once you've read them, you're on your own. Nobody answers your questions, and nobody tells you what to do next. With Pro, you get a coach who knows your plan and walks you through it, step by step." },
];

const STEPS = [
  "Answer 14 quick questions.",
  "See the paths that match you best, each with a match score.",
  "Choose the one that fits you.",
  "Let your AI coach help you build it step by step.",
];

const StartButton = ({ testid, size = "text-xl sm:text-2xl px-8 py-5", label = "SHOW ME MY TOP 3 SIDE HUSTLES" }) => (
  <Link to="/quiz" className={`block w-full sm:w-auto sm:inline-block hero-accent text-white font-display font-black ${size} rounded-lg text-center rot-hover`} data-testid={testid}>
    {label}
  </Link>
);

const Section = ({ children, testid }) => (
  <section className="max-w-3xl mx-auto px-5 py-8 sm:py-10 border-t border-[#2A2E39]" data-testid={testid}>{children}</section>
);

const Blocks = ({ items, prefix }) => (
  <div className="space-y-3">
    {items.map((x, i) => (
      <div key={i} className="card-panel px-5 pt-3 pb-5" data-testid={`${prefix}-${i}`}>
        <div className="font-display text-lg sm:text-xl font-black text-[#FD0F10] leading-tight">{x.h}</div>
        <p className="mt-2 text-base sm:text-lg text-gray-100 leading-relaxed">{x.t}</p>
        {x.small && <p className="mt-2 text-xs text-gray-400" data-testid={`${prefix}-${i}-small`}>{x.small}</p>}
      </div>
    ))}
  </div>
);

const PathExample = () => (
  <section className="max-w-3xl mx-auto px-5 pb-8" data-testid="product-preview">
    <div className="eyebrow normal-case text-[#FD0F10]">What you get</div>
    <div className="mt-3 card-panel p-4 max-w-xl" data-testid="preview-path-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-400 font-mono-ui uppercase"><TrendingUp size={13} /> Your #1 path</div>
        <span className="text-[10px] font-mono-ui uppercase tracking-wider text-gray-400 border border-[#2A2E39] rounded px-1.5 py-0.5">Example</span>
      </div>
      <div className="mt-2 font-display text-xl font-bold text-white">Digital Products (ebooks and printables)</div>
      <p className="mt-1 text-sm text-gray-300">Why it fits: you can start with $0 using free tools, work from your phone or laptop after your shift, and you don't need to talk to customers.</p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-2"><div className="text-gray-500">TIME</div><div className="text-white">Fits your 1 to 3 hrs/week</div></div>
        <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-2">
          <div className="text-gray-500">FIRST 3 MONTHS</div><div className="text-white">$0 to $150/month</div>
          <div className="text-gray-400 mt-1" data-testid="preview-more-time">Putting in more time usually means more money.</div>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-gray-500">Results depend on your effort. The more hours you put in, the faster it usually grows. Most people earn less at first, and that's normal.</p>
    </div>
  </section>
);

const FREE_NOTE = "Free to start. No card needed. No surprise charges.";
const TAGS = ["Local Service Business", "Online Tutoring", "Amazon KDP Self-Publishing", "UGC Creator", "Video Editing for Creators", "Small Business Bookkeeping"];

const PathTags = () => (
  <span className="mt-3 flex flex-wrap gap-2" data-testid="how-step-path-tags">
    {TAGS.map(t => <span key={t} className="text-xs text-gray-200 border border-[#2A2E39] bg-[#0B0C0E] rounded-full px-2.5 py-1">{t}</span>)}
  </span>
);

// Phones only: start button pinned to the bottom once the hero button scrolls away.
const StickyStart = ({ show }) => (
  <div className={`sm:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[#0B0C0E]/90 backdrop-blur border-t border-[#2A2E39] transition-transform duration-200 ${show ? "translate-y-0" : "translate-y-full"}`} data-testid="sticky-start">
    <StartButton testid="cta-sticky" size="text-base px-4 py-3" />
  </div>
);

export default function LandingPreview() {
  const { user } = useAuth();
  const heroBtn = useRef(null);
  const [sticky, setSticky] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setSticky(!e.isIntersecting && e.boundingClientRect.top < 0));
    if (heroBtn.current) io.observe(heroBtn.current);
    return () => io.disconnect();
  }, []);

  return (
    <div className="relative pb-20 sm:pb-0" data-testid="landing-preview">
      <ResumeBanner />
      <section className="max-w-3xl mx-auto px-5 pt-3 pb-8 sm:pt-10 sm:pb-10">
        <h1 className="m-0" data-testid="hero-h1">
          <img src="/logo.webp" alt="I Hate My Job" className="mx-auto w-[40%] max-w-[260px] h-auto select-none" style={{ mixBlendMode: "lighten" }} draggable="false" />
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[#FD0F10] font-bold" data-testid="hero-kicker">Do you hate your job but have no idea what to do?</p>
        <p className="mt-1 font-display text-3xl sm:text-5xl font-black text-white leading-[1.05]" data-testid="hero-headline">
          <span className="block">Your job pays the bills.</span>
          <span className="block">Your plan gets you out.</span>
        </p>
        <p className="mt-3 text-base sm:text-lg text-gray-200 leading-snug" data-testid="hero-subline">Answer 14 quick questions. In a few minutes, you'll see your top 3 side hustles, matched to your skills, your budget, and the time you actually have.</p>
        <div className="mt-5" ref={heroBtn}><StartButton testid="cta-find-way-out" size="text-lg sm:text-xl px-6 py-3.5" /></div>
        <p className="mt-2 text-sm text-gray-300 text-center sm:text-left" data-testid="hero-free-note">{FREE_NOTE}</p>
        {!user && (
          <p className="mt-1 text-center sm:text-left">
            <Link to="/auth" className="text-xs text-gray-400 underline underline-offset-4 hover:text-white" data-testid="cta-sign-in">I already have an account</Link>
          </p>
        )}
      </section>

      <PathExample />

      <PathShots />

      <Section testid="outcome-section">
        <Blocks items={OUTCOMES} prefix="outcome" />
      </Section>

      <Section testid="pain-section">
        <Blocks items={PAIN_POINTS} prefix="pain" />
      </Section>

      <Section testid="founder-section">
        <h2 className="font-display text-2xl sm:text-3xl font-black text-[#FD0F10] leading-tight" data-testid="founder-heading">Why I built this</h2>
        <div className="mt-4 space-y-4 text-base sm:text-lg text-gray-100 leading-relaxed" data-testid="why-i-built-this">
          <p>For decades, I watched people drag themselves to jobs they hated, and it wore them down. I always believed that if I didn't like where I was, I'd grow and find something better. So that's what I did. I started my own pressure washing business, then spent 10 years learning to build online.</p>
          <p className="text-white font-bold">I built this app to give others that same chance, to stop feeling stuck and start building something they actually want.</p>
          <p className="font-display font-black text-white" data-testid="founder-signature">— Chris</p>
        </div>
      </Section>

      <Section testid="transition-section">
        <h2 className="font-display text-2xl sm:text-4xl font-black leading-tight" data-testid="transition-headline">
          <span className="block">You already know why you want out.</span>
          <span className="block">The real question is: what do you do next?</span>
        </h2>
        <p className="mt-4 text-base sm:text-lg text-gray-100">This app gives you a plan, and a coach who helps you follow it.</p>
        <ol className="mt-5 space-y-3" data-testid="how-it-works">
          {STEPS.map((t, i) => (
            <li key={i} className="flex gap-3 items-start" data-testid={`how-step-${i}`}>
              <span className="shrink-0 w-8 h-8 rounded-full hero-accent text-white font-black flex items-center justify-center">{i + 1}</span>
              <span className="text-base sm:text-lg text-gray-100 leading-snug pt-1">
                {t}
                {i === 1 && <span className="block mt-2 text-sm sm:text-base text-gray-300" data-testid="how-step-paths-note">Your best matches are picked from 28 proven side hustle paths, based on what you like to do and your budget. Answer the questions honestly, so the app can find the paths that really fit you.</span>}
                {i === 1 && <PathTags />}
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-6 font-display text-xl sm:text-2xl font-black text-white leading-tight" data-testid="transition-close">Don't quit tomorrow. Start building your way out today.</p>
        <div className="mt-4"><StartButton testid="cta-how-it-works" size="text-lg sm:text-xl px-6 py-3.5" /></div>
      </Section>

      <Section testid="plans-section">
        <Blocks items={PLAN_BLOCKS} prefix="plan" />
      </Section>

      <Section testid="honesty-section">
        <p className="text-base sm:text-lg text-gray-100 leading-relaxed" data-testid="honesty-line">This isn't magic or free money.</p>
        <p className="mt-5 text-base sm:text-lg text-white font-bold leading-relaxed" data-testid="mission-line">The goal is simple. We want everyone who uses this app to win. That's why we kept Pro, with full 24/7 coach access, at $19 USD a month. That's about 63 cents a day, roughly the price of one coffee a week. Price should never be the reason you stay stuck. You can also pay $149 a year, which gets you four months free. You can cancel anytime.</p>
      </Section>

      <Section testid="faq-section">
        <div className="eyebrow text-[#FD0F10]">FAQ</div>
        <Accordion type="single" collapsible defaultValue="faq-0" className="mt-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#2A2E39]">
              <AccordionTrigger className="font-display text-lg text-white hover:no-underline" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-base text-gray-300 leading-relaxed" data-testid={`faq-a-${i}`}>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <Section testid="final-section">
        <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.05]" data-testid="final-headline">How many more shifts are you giving away?</h2>
        <div className="mt-6"><StartButton testid="cta-signup-full-plan" label="Take the free 2-minute quiz →" /></div>
        <p className="mt-2 text-sm text-gray-300 text-center sm:text-left" data-testid="final-free-note">{FREE_NOTE}</p>
        <p className="mt-10 text-[11px] text-gray-500 leading-relaxed text-center" data-testid="landing-fineprint">
          Results depend on your effort, time, consistency, and factors outside our control. Nothing here is a guarantee. <Link to="/terms" className="underline hover:text-gray-400" data-testid="landing-terms-link">Read the full terms and disclaimer</Link>. Questions, cancellations, or data requests? Email us at <a href="mailto:info@ihatemyjob.app" className="underline hover:text-gray-400" data-testid="contact-email-link">info@ihatemyjob.app</a>.
        </p>
      </Section>
      <StickyStart show={sticky} />
    </div>
  );
}
